import Page from '../../layout/Page';
import { useAuth, useCompany } from '../../firebase/auth';
import { Timestamp, addDoc, collection, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/DataTable';
import { format } from 'date-fns';
import StatusTag from '../../components/StatusTag';
import { useState } from 'react';
import LevelSelectForm from '../../forms/levels/LevelSelectForm';
import QRLink from '../../components/QRLink';
import RiskTag from '../../components/RiskTag';
import CustomerFilterForm from '../../forms/customers/CustomerFilterForm';
import CustomerCreateForm from '../../forms/customers/CustomerCreateForm';
import Header from '../../layout/Header';

import DialogTitle from '@mui/joy/DialogTitle';
import ModalClose from '@mui/joy/ModalClose';
import Button from '@mui/joy/Button';
import Grid from '@mui/joy/Grid';
import Chip from '@mui/joy/Chip';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import Drawer from '@mui/joy/Drawer';
import Stack from '@mui/joy/Stack';

import Add from '@mui/icons-material/Add';
import { useSnackbar } from '../../utils/SnackbarContext';

export default function Customers() {
    const { authUser } = useAuth();
    const { authCompany } = useCompany();
    const showSnackbar = useSnackbar();

    const [showModal, setShowModal] = useState(false);
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [showCreateDrawer, setShowCreateDrawer] = useState(false);
    const [link, setLink] = useState(null);
    const [linkLoading, setLinkLoading] = useState(false);
    const [levels, setLevels] = useState(null);
    const [filters, setFilters] = useState({
        status: [],
        level: [],
        risk: [],
        country: [],
        date: {
            start: '',
            end: ''
        }
    });
    const [filterConstraints, setFilterConstraints] = useState([]);
    const [refresh, setRefresh] = useState(false);

    const navigate = useNavigate();

    const navigateToCustomerDetails = (customerId) => {
        navigate("./"+customerId, { relative: "path"});
    }

    const handleClose = () => {
        setShowModal(false);
        setLink(null);
        setShowCreateDrawer(false);
        setSelectedLevel(null);  // Clear selected level when closing the drawer
    }
    
    const handleShow = () => {
        setSelectedLevel(null);  // Clear selected level when showing the modal
        setShowModal(true);
    };

    const handleLevelSelect = (level) => {
        setSelectedLevel(level);
    }

    const handleCreateLink = () => {
        const link = {
            level: {
                id: selectedLevel.id,
                name: selectedLevel.name,
                documents: selectedLevel.documents,
                fields: selectedLevel.fields,
                verifications: selectedLevel.verifications,
                type: selectedLevel.type
            },
            type: selectedLevel.type,
            company: authUser.company.uid,
            companyName: authCompany.name,
            expirationDate: new Date(Timestamp.now().seconds * 1000 + 86400000),
            createdBy: authUser.uid,
            createdByName: authUser.displayName
        }

        if (authCompany?.subscription?.limits?.customers <= 0) {
            showSnackbar("You have reached the limit of KYC verifications allowed in your plan", "danger");
            return;
        }

        setLinkLoading(true);
        const linkRef = collection(db, 'links');
        addDoc(linkRef, link)
        .then((docRef) => {
            setLink(window.location.origin + "/link/kyc/" + docRef.id);
        })
        .catch((error) => {
            showSnackbar("An error occurred: " + error, "danger");
        })
        .finally(() => {
            setLinkLoading(false);
        });
    }

    const headerButtons = () => {
        return (
            <Button
                size="sm"
                variant="solid"
                color="primary"
                startDecorator={<Add />}
                onClick={handleShow}
            >
                Create New
            </Button>
        );
    }

    const onComplete = () => {
        setShowCreateDrawer(false);
        setShowModal(false);
        setSelectedLevel(null);  // Clear selected level after completing an action
        setRefresh(prev => !prev);
    }

    const dataQuery = query(collection(db, 'companies', authUser.company.uid, 'customers'), orderBy("updatedAt", "desc"));
    const searchField = "keywords";
    const columns = ["Customer","Status","Tags","Risk","Level","Date"];
    const pageSize = 10;
    const rowRenderer = (customer) => {
        return (
          <tr key={customer.uid} onClick={() => navigateToCustomerDetails(customer.uid)} style={{cursor: "pointer"}}>
            <td>
                <Typography fontWeight={500}>
                    {customer.firstName} {customer.lastName}
                </Typography>
            </td>
            <td><StatusTag status={customer.status.status}/></td>
            <td>
                { customer.status.tags && customer.status.tags.length > 0 && customer.status.tags.slice(0, 2).map((tag) => (
                    <Chip key={tag} variant="soft" color={customer.status.status === "rejected" ? "danger" : "neutral"} size="sm">{tag}</Chip>
                ))}
                { customer.status.tags && customer.status.tags.length > 2 &&
                    <Tooltip id={`tooltip-${customer.uid}`} variant="plain" title={
                        customer.status.tags.slice(2).map((tag) => (
                            <Typography level="body-xs">{tag}</Typography>
                        ))
                    }>
                        <Chip variant="soft" color={customer.status.status === "rejected" ? "danger" : "neutral"} size="sm">+{customer.status.tags.length - 2}</Chip>
                    </Tooltip>
                }
            </td>
            <td><RiskTag risk={customer.status.risk}/></td>
            <td>{customer.level.name}</td>
            <td>
                {format(customer.createdAt.toDate(), "dd/MM/yyyy' 'HH:mm")}
            </td>
          </tr>
        );
    };

    const handleFilters = (e) => {
        const q = [];
        if(e.status?.length > 0){
            q.push(where("status.status", "in", e.status));
        }
        if(e.level?.length > 0){
            q.push(where("level.name", "==", e.level));
        }
        if(e.risk?.length > 0){
            q.push(where("status.risk", "in", e.risk));
        }
        if(e.country?.length > 0){
            q.push(where("nationality", "==", e.country));
        }
        if(e.date?.start && e.date?.start !== ''){
            q.push(where("createdAt", ">=", Timestamp.fromDate(new Date(e.date.start))));
        }
        if(e.date?.end && e.date?.end !== ''){
            q.push(where("createdAt", "<=", Timestamp.fromDate(new Date(e.date.end))));
        }
        setFilters(e);
        setFilterConstraints(q);
    }

    return (
        <Page>
            <Header title="Customers" controls={headerButtons()}/>
            <Grid>
                <Grid xs={12}>
                    <DataTable
                        key={refresh}
                        tableTitle="Customers"
                        dataQuery={dataQuery}
                        columns={columns}
                        rowRenderer={rowRenderer}
                        pageSize={pageSize}
                        searchField={searchField}
                        filterForm={<CustomerFilterForm levels={levels} setLevels={setLevels} filters={filters} handleFilters={handleFilters}/>}
                        filters={filterConstraints}
                        setFilters={setFilters}
                    />
                </Grid>
            </Grid>
            <Drawer anchor="right" open={showModal} onClose={handleClose}>
                <ModalClose />
                <DialogTitle>Create Customer</DialogTitle>
                <Stack direction="column" p={1.5} spacing={1}>
                { showCreateDrawer && selectedLevel ?
                    <CustomerCreateForm level={selectedLevel} onComplete={onComplete}/>
                    :
                    link ?
                    <QRLink link={link} onClose={handleClose}/>
                    :
                    <>
                    <LevelSelectForm type="kyc" handleOnChange={handleLevelSelect} selectedLevel={selectedLevel} />
                    <Button
                        size="sm"
                        disabled={!selectedLevel}
                        onClick={()=>setShowCreateDrawer(true)}
                    >
                        Create Customer
                    </Button>
                    <Button
                        size="sm"
                        variant="soft"
                        loading={linkLoading}
                        disabled={!selectedLevel || linkLoading}
                        onClick={handleCreateLink}>
                            Create Link
                    </Button>
                    </>
                }
                </Stack>
            </Drawer>
        </Page>
    );
}
